import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { InquiriesService } from 'src/shared/services/inquiries.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

/**
 * Reusable "Inquiries" review alert: a compact one-line pill (icon · title ·
 * count · avatars · Review) showing how many inquiries are waiting on the
 * host's reply (across channels — Airbnb, VRBO, Direct Booking, …), tapping
 * through to the Inquiries page.
 *
 * "Waiting on your reply" = inquiries the host can still act on (pre-approve or
 * decline); read-only channel rows don't count toward the badge.
 */
@Component({
  selector: 'booking-requests-banner',
  templateUrl: './booking-requests-banner.component.html',
  styleUrls: ['./booking-requests-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule],
})
export class BookingRequestsBannerComponent {
  // Render nothing when there's nothing waiting (e.g. on the Schedule page).
  @Input() hideWhenEmpty = false;

  private static readonly MAX_PREVIEW = 3;

  private readonly service = inject(InquiriesService);
  private readonly navCtrl = inject(CustomNavController);

  readonly pendingCount = signal<number>(0);
  readonly previewInitials = signal<string[]>([]);

  // Fetch in the constructor so takeUntilDestroyed() resolves its DestroyRef
  // from the guaranteed injection context.
  constructor() {
    this.service
      .getInquiries()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (inquiries) => {
          const waiting = inquiries.filter((i) => i.available_actions?.length > 0);
          this.pendingCount.set(waiting.length);
          this.previewInitials.set(
            waiting
              .slice(0, BookingRequestsBannerComponent.MAX_PREVIEW)
              .map((i) => this.initials(i.guest?.name))
          );
        },
        error: () => {
          this.pendingCount.set(0);
          this.previewInitials.set([]);
        },
      });
  }

  goToRequests = (): void => {
    this.navCtrl.navigateForward('inquiries');
  };

  private initials(name: string | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
}
